import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteContent } from '../context/SiteContentContext';
import { Users, GraduationCap, Shield, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const Login = () => {
  const { siteContent } = useSiteContent();
  const loginPage = siteContent?.loginPage || {};

  const roles = [
    { 
      icon: Users, 
      titleKey: 'studentLabel',
      color: 'bg-blue-50 text-blue-600', 
      link: null, 
      comingSoon: true 
    },
    { 
      icon: GraduationCap, 
      titleKey: 'teacherLabel',
      color: 'bg-green-50 text-green-600', 
      link: null, 
      comingSoon: true 
    },
    { 
      icon: Shield, 
      titleKey: 'adminLabel',
      color: 'bg-slate-100 text-slate-700', 
      link: '/admin/login', 
      comingSoon: false 
    }
  ];

  return (
    <div data-testid="login-page">
      {/* Header */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4 block">
              {loginPage.headerLabel || 'Account'}
            </span>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
              {loginPage.title || 'Login'}
            </h1>
            <p className="text-lg text-slate-600">
              {loginPage.body || 'Student / Teacher / Admin login coming soon. Our full learning platform is under development.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Roles */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {roles.map((role, index) => {
              const Icon = role.icon;
              const title = loginPage[role.titleKey] || role.titleKey;
              const roleKey = role.titleKey || `role-${index}`;
              
              const CardContent = (
                <div className={`p-8 rounded-xl border border-slate-100 text-center transition-all ${
                  role.link ? 'hover:border-slate-300 hover:shadow-sm cursor-pointer' : 'opacity-60'
                }`}>
                  <div className={`w-14 h-14 ${role.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">{title}</h3>
                  {role.comingSoon ? (
                    <span className="text-xs text-slate-400 uppercase tracking-wider">
                      {loginPage.comingSoon || 'Coming Soon'}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-600 flex items-center justify-center gap-1">
                      {loginPage.signIn || 'Sign in'} <ArrowRight className="w-3 h-3" />
                    </span>
                  )}
                </div>
              );

              return (
                <motion.div
                  key={roleKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.5, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  {role.link ? (
                    <Link to={role.link} data-testid={`login-admin-card`}>
                      {CardContent}
                    </Link>
                  ) : (
                    CardContent
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
